import { IconButton, Theme, Tooltip } from '@mui/material';
import { grey } from '@mui/material/colors';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import Colors from '../../../utils/color';
import TruncationTooltip from './TruncationTooltip';
import { COUNT_CHIP_STYLE } from './countChip.styles';

/**
 * HITL-1696 — the legacy 3.0 chip, value for value, so "Boxes" reads as the
 * boxes agents learned on. Every figure is lifted from `SingularModChoice.tsx`
 * (identical in `MultipleModChoice.tsx`) or from the MUI `Chip` it rendered:
 *
 * - 40px tall, radius 4px, bold 12px, centred, clamped to two lines, 8px label
 *   padding — `chipContainer`.
 * - Unselected: MUI's outlined chip — `1px solid grey[400]`, no fill, primary
 *   text. Selected: `mineShaft` fill, white text; the chip's border is
 *   `currentColor`, so it goes white with the text.
 * - The group's default option: the border turns `2px dashed`.
 * - 86'd: `Colors.red` + line-through, `cursor: default`.
 * - No hover: the chip had no onClick of its own, so MUI painted none.
 *
 * With `boxed` off not one key below is added, so the class — and every
 * snapshot — is byte-identical to the HITL-1616 row.
 */
const boxStyles = (
  theme: Theme,
  isSelected: boolean,
  is86ed: boolean,
  isDefault: boolean
) => ({
  justifyContent: 'center',
  height: theme.spacing(5),
  padding: '0 8px',
  boxSizing: 'border-box' as const,
  borderRadius: theme.spacing(0.5),
  borderStyle: isDefault ? 'dashed' : 'solid',
  borderWidth: isDefault ? '2px' : '1px',
  borderColor: isSelected ? Colors.white : grey[400],
  backgroundColor: isSelected ? Colors.mineShaft : 'transparent',
  color: is86ed
    ? Colors.red
    : isSelected
    ? Colors.white
    : theme.palette.text.primary,
  fontWeight: 700,
  cursor: is86ed ? 'default' : 'pointer',
  '&:hover': { backgroundColor: isSelected ? Colors.mineShaft : 'transparent' },
});

interface OptionStyleProps {
  isSelected: boolean;
  is86ed: boolean;
  boxed: boolean;
  isDefault: boolean;
  caps: boolean;
  fontSize: number | null;
}

const useStyles = makeStyles<OptionStyleProps>()(
  (theme, { isSelected, is86ed, boxed, isDefault, caps, fontSize }) => ({
    // 24px borderless row, against the 40px chip locked to `width: 18%` it
    // replaces. The box was worth about 1px of row height, so this is about ink,
    // not space.
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      minWidth: 0,
      height: 24,
      borderRadius: 2,
      cursor: is86ed ? 'not-allowed' : 'pointer',
      color: is86ed ? Colors.Thunderbird : Colors.daintree,
      fontWeight: isSelected ? 700 : 400,
      // Which row the pointer is on. The rows are borderless and tightly
      // packed, so without this there is nothing to tell the agent what a click
      // is about to land on.
      '&:hover': {
        backgroundColor: is86ed ? 'transparent' : 'rgba(0, 0, 0, 0.06)',
      },
      ...(boxed ? boxStyles(theme, isSelected, is86ed, isDefault) : {}),
    },
    // Moved to the LEFT edge, where the selection tick used to sit: the group
    // row's own caret is on the left, so a marker on the right made the two
    // rows disagree about where a disclosure lives.
    //
    // And it rotates, exactly as `ModGroupRow.caret` does. Selecting an option
    // is what reveals its further groups — `RecursiveModGroup` renders the
    // children of options the cart holds — so a selected option's groups are
    // open below it. Sharing the glyph with the group caret while staying
    // permanently side-on made the same mark mean "collapsed" in one row and
    // nothing at all in the next.
    furtherGroups: {
      display: 'inline-block',
      transform: isSelected ? 'rotate(90deg)' : 'none',
      transition: 'transform 120ms ease',
      flexShrink: 0,
      // 8px, and the glyph centred in it. The box has to be a fixed width so
      // every name in the drawer starts at the same x, but › is only about
      // 4px wide — at 12px and left-aligned it left ~8px of dead space inside
      // the box, on top of the row gap, which read as a large hole between the
      // marker and the name.
      width: 8,
      textAlign: 'center',
      fontSize: '12px',
      color: Colors.silverChalice,
      // On the dark selected box the grey marker vanished; the chip's own
      // text colour is what it sat in.
      ...(boxed && isSelected ? { color: 'inherit' } : {}),
    },
    // The strike belongs to the NAME, not the row. On the row it also drew
    // through the fixed-width marker cell to the left of the text, which
    // rendered as a stray red dash in front of every unavailable option.
    name: {
      // `0 1 auto`, NOT `1`: at flex-grow 1 the name stretched to the full
      // column width and shoved the info button out to the far right edge,
      // a long way from the option it belongs to — and, in a 2 or 3 column
      // drawer, closer to the NEXT column's rows than to its own text. Sized to
      // its content, the button follows the name with the row's own 4px gap,
      // and still shrinks with an ellipsis when the name is too long.
      flex: '0 1 auto',
      minWidth: 0,
      fontSize: '13px',
      textDecoration: is86ed ? 'line-through' : 'none',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      // HITL-1696: the menu's own case unless the agent chose capitals. CSS
      // only — the string itself still feeds the tooltip and TTS unchanged.
      ...(caps ? { textTransform: 'uppercase' as const } : {}),
      // The chip wrapped to two centred lines rather than ellipsising one.
      ...(boxed
        ? {
            fontSize: theme.spacing(1.5),
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical' as const,
            whiteSpace: 'normal',
            textAlign: 'center' as const,
            textOverflow: 'clip',
          }
        : {}),
      // HITL-1696: the size the agent picked, overriding either style's own.
      // In a box, a tighter line keeps two lines of 15px inside the 40px the
      // chip was — at the inherited line height the second line would clip.
      ...(fontSize
        ? {
            fontSize: `${fontSize}px`,
            ...(boxed ? { lineHeight: 1.2 } : {}),
          }
        : {}),
    },
    info: {
      padding: 0,
      color: 'inherit',
    },
    // The tooltip needs a live anchor. MUI gives a disabled button
    // `pointer-events: none`, so with the button as the tooltip's direct child
    // the hover would be swallowed once the option is 86'd (and MUI logs a
    // warning for it). The span takes the hover; the button takes the press.
    infoWrap: {
      flexShrink: 0,
      display: 'inline-flex',
    },
  })
);

interface ModOptionRowProps {
  name: string;
  isSelected: boolean;
  is86ed: boolean;
  bringsFurtherGroups: boolean;
  onSelect: () => void;
  /** The option's spoken description, when it has one. */
  description?: string;
  /** Reads `description` out to the customer. Absent when there is none. */
  onInfoClick?: () => void;
  /** HITL-1696: paint the row as the 3.0 chip (the agent chose "Boxes"). */
  boxed?: boolean;
  /** HITL-1696: the group's default option — dashed, as the chip was. Boxes only. */
  isDefault?: boolean;
  /** HITL-1696: CSS-uppercase the name (the agent chose "CAPITALS"). */
  caps?: boolean;
  /** HITL-1696: the name size the agent picked, in px; absent = the style's own. */
  fontSize?: number | null;
  /**
   * HITL-1753: how many of this option, per unit of the item. Read only while
   * `onQuantityChange` is set.
   */
  quantity?: number;
  /** HITL-1753: the most the stepper allows (`getModifierMaxQuantity`). */
  maxQuantity?: number;
  /**
   * HITL-1753: set, a selected row draws the count stepper. Absent — flag off,
   * off the brand menu, a slot — and the row is exactly the HITL-1616 row.
   */
  onQuantityChange?: (quantity: number) => void;
}

// HITL-1753: a `−` / `+` glyph. Inline, not a JSS rule — a new rule in this
// sheet would renumber class names across unrelated snapshots.
const stepGlyphStyle = (
  color: string,
  enabled: boolean
): React.CSSProperties => ({
  flexShrink: 0,
  fontWeight: 700,
  padding: '0 2px',
  color,
  cursor: enabled ? 'pointer' : 'default',
  userSelect: 'none',
});

/**
 * HITL-1753 — the count stepper on a selected option.
 *
 * Quiet until the count matters: at ×1 only a faint `+` (every selected option
 * can take a count, so a full `− ×1 +` on the size, the flavour and "black"
 * would be noise). From ×2 it is `− ×N +`, the bagel panel's chip and `−`.
 * `−` at ×2 folds back to the lone `+`; taking the option away is still a tap
 * on the row, so the row keeps the meaning agents already have for it.
 */
const QuantityStepper = ({
  name,
  quantity,
  max,
  onChange,
  onDark,
}: {
  name: string;
  quantity: number;
  max: number;
  onChange: (quantity: number) => void;
  /** The boxed, selected chip is mineShaft: glyphs go white on it. */
  onDark: boolean;
}) => {
  const canAdd = quantity < max;
  const ink = onDark ? Colors.white : Colors.daintree;
  const step =
    (next: number, enabled: boolean) =>
    (event: React.MouseEvent | React.KeyboardEvent) => {
      // The row toggles the selection; a count change must not.
      event.stopPropagation();
      if (enabled) onChange(next);
    };
  const keyed =
    (next: number, enabled: boolean) => (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ')
        step(next, enabled)(event);
    };

  return (
    <span
      data-testid="mod-option-quantity"
      style={{
        marginLeft: 'auto',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        flexShrink: 0,
      }}
    >
      {quantity > 1 && (
        <>
          <span
            role="button"
            tabIndex={0}
            aria-label={`Fewer ${name}`}
            data-testid="mod-option-quantity-minus"
            style={stepGlyphStyle(ink, true)}
            onClick={step(quantity - 1, true)}
            onKeyDown={keyed(quantity - 1, true)}
          >
            −
          </span>
          <span
            style={COUNT_CHIP_STYLE}
            data-testid="mod-option-quantity-count"
          >
            ×{quantity}
          </span>
        </>
      )}
      <span
        role="button"
        tabIndex={canAdd ? 0 : -1}
        aria-label={`More ${name}`}
        aria-disabled={!canAdd}
        data-testid="mod-option-quantity-plus"
        style={stepGlyphStyle(
          !canAdd
            ? Colors.alto
            : quantity > 1
            ? ink
            : onDark
            ? Colors.white
            : Colors.silverChalice,
          canAdd
        )}
        onClick={step(quantity + 1, canAdd)}
        onKeyDown={keyed(quantity + 1, canAdd)}
      >
        +
      </span>
    </span>
  );
};

/**
 * HITL-1616 — one option inside the drawer.
 *
 * Selection is weight alone: the tick that used to sit on the left was a fixed
 * 12px of gutter on every row, spent to say something bold already says. The
 * `›` disclosure took its place so it lines up with the group row's caret.
 *
 * An unavailable option is red + line-through with **no "86" badge** — the
 * `SingularModChoice` precedent, kept.
 *
 * The name is printed in the menu's own case, NOT uppercased the way the chips
 * were. Caps read measurably slower and run about 10-15% wider, and these are
 * `minmax(186px, 1fr)` columns that ellipsise — so uppercasing would truncate
 * the long names this row exists to spell out. And never vowel-stripped:
 * `getModGroupItemLabel` rendered "Substitute Sugar Free Vanilla" as
 * `Sbsttt Sgr Fr Vnll`.
 *
 * No price. The frozen design carries none, and the console could not show a
 * correct one anyway: both chip callers pass no `modalityState`, so
 * `getMenuItemPrice` falls back to `ModalityType.Dinein` on a drive-thru. Prices
 * stay where they are true — the cart line.
 */
const ModOptionRow = ({
  name,
  isSelected,
  is86ed,
  bringsFurtherGroups,
  onSelect,
  description,
  onInfoClick,
  boxed = false,
  isDefault = false,
  caps = false,
  fontSize = null,
  quantity = 1,
  maxQuantity = 1,
  onQuantityChange,
}: ModOptionRowProps) => {
  const { classes } = useStyles({
    isSelected,
    is86ed,
    boxed,
    isDefault: boxed && isDefault,
    caps,
    fontSize,
  });

  const onClick = () => {
    if (is86ed) return;
    onSelect();
  };

  return (
    <div
      className={classes.row}
      onClick={onClick}
      data-testid="mod-option-row"
      data-selected={isSelected ? 'true' : 'false'}
      role="button"
      tabIndex={is86ed ? -1 : 0}
      aria-disabled={is86ed}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onClick();
      }}
    >
      {/*
        Always rendered, so every name in the drawer starts at the same x
        whether or not its option opens further groups. The test id is attached
        only when the marker is really there — on an empty spacer it would make
        the "brings further groups" assertion pass for nothing.
      */}
      <span
        className={classes.furtherGroups}
        data-testid={bringsFurtherGroups ? 'brings-groups' : undefined}
        aria-hidden={!bringsFurtherGroups}
      >
        {bringsFurtherGroups ? '›' : ''}
      </span>
      <TruncationTooltip title={name}>
        <span className={classes.name}>{name}</span>
      </TruncationTooltip>
      {onInfoClick && (
        <Tooltip title={description || ''} arrow placement="top">
          <span className={classes.infoWrap}>
            <IconButton
              size="small"
              className={classes.info}
              // An unavailable option cannot be ordered, so its description
              // must not be speakable to the customer either — the row itself
              // already refuses the click, and this closes the one control that
              // still reached past it. Kept rendered rather than removed, so
              // the row still shows that a description exists.
              disabled={is86ed}
              // The row toggles the selection, so without this the icon would
              // pick the option as well as speak it.
              onClick={(event) => {
                event.stopPropagation();
                onInfoClick();
              }}
              aria-label={`Play description for ${name}`}
            >
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {onQuantityChange && isSelected && !is86ed && maxQuantity > 1 && (
        <QuantityStepper
          name={name}
          quantity={quantity}
          max={maxQuantity}
          onChange={onQuantityChange}
          onDark={boxed}
        />
      )}
    </div>
  );
};

export default ModOptionRow;
